import { get } from 'lodash';
import type { Invoice } from '../types';
import type { AppealItem } from '../types';

/**
 * Calculates the agreed to pay amount for an invoice and appeal combination.
 *
 * The calculation logic:
 * - If the invoice's appeal ID matches the appeal's ID, calculates:
 *   totalInvoice - writeOffAmount - lastCollectedAmount - amountToAppeal - paid
 * - Otherwise, returns the appeal's finalAgreedToPay value
 *
 * @param invoice - The invoice containing total, paid, writeOffAmount, and appeal data
 * @param appeal - The appeal item containing appealId and finalAgreedToPay
 * @returns The calculated agreed to pay amount (rounded to 2 decimal places)
 *
 * @example
 * ```typescript
 * const amount = calculateAgreedToPay(invoiceData, appeal);
 * // Returns: 1234.56
 * ```
 */
export function calculateAgreedToPay(invoice: Invoice | undefined, appeal: AppealItem): number {
  // Return 0 if invoice is undefined
  if (!invoice) {
    return get(appeal, 'finalAgreedToPay', 0);
  }
  const totalInvoice = get(invoice, 'total', 0);
  const invoiceAppealId = get(invoice, 'appeal.appealId', '');
  // If the invoice's appeal ID matches the appeal's ID, calculate based on invoice data
  if (invoiceAppealId === appeal.appealId) {
    const lastCollectedAmount = get(invoice, 'appeal.lastCollectedAmount', 0);
    const writeOffAmount = get(invoice, 'writeOffAmount', 0);
    const feesCut = get(invoice, 'appeal.feesCut', 0);
    const expensescut = get(invoice, 'appeal.expensesCut', 0);
    const approvedFees = get(invoice, 'appeal.approvedFees', 0);
    const approvedExpenses = get(invoice, 'appeal.approvedExpenses', 0);
    const totalApproved = approvedFees + approvedExpenses;
    const calculatedAmount =
      totalInvoice + totalApproved - writeOffAmount - lastCollectedAmount - feesCut - expensescut;

    const rounded = Number(calculatedAmount.toFixed(2));

    return rounded <= 0 ? 0 : rounded;
  }

  // Otherwise, return the appeal's finalAgreedToPay value
  return get(appeal, 'finalAgreedToPay', 0);
}
